import {
  AgentCompiler,
  type ValidationOptions,
} from '@pypestream/agent-compiler';
import { isEqual } from 'lodash';
import type { TableNode } from '..';

type DiagnosticCode = unknown;

export type Diagnostic = {
  code?: string | number | DiagnosticCode;
  codeDescription?: string;
  data?: {
    id?: string;
    nodeNumber?: string;
    propertyName?: string;
  };
  message: string;
  severity?: number;
  range: {
    start: { line: number | null; character: number | null };
    end: { line: number | null; character: number | null };
  };
  relatedInformation?: unknown;
  source?: string;
  tags?: unknown;
};

type PrepareRowsFn = (rows: TableNode[]) => string;

/**
 * Service for partial PIF validation using PifValidator.
 * Tracks changes between validations and only sends changed/added nodes
 * or node IDs to remove, reducing load on large solutions.
 */
export class PifPartialValidationService {
  private validator: AgentCompiler;
  private options?: ValidationOptions;
  private lastValidatedNodeMap = new Map<string, Record<string, unknown>>();
  private lastValidatedSolutionVersionId = '';
  private lastDiagnostics: Diagnostic[] = [];

  constructor(options?: ValidationOptions) {
    this.options = options;
    this.validator = new AgentCompiler(options);
  }

  /**
   * Update script file names passed to the validator.
   * Call whenever the list of uploaded scripts changes so the compiler can
   * report missing-script errors accurately.
   */
  updateScriptFileNames(fileNames: string[]): void {
    const prevSorted = [...(this.options?.scriptFileNames ?? [])]
      .sort()
      .join(',');
    const nextSorted = [...fileNames].sort().join(',');
    const hasChanged = prevSorted !== nextSorted;

    this.options = { ...(this.options ?? {}), scriptFileNames: fileNames };
    this.validator.setValidatorOptions(this.options);

    if (hasChanged) {
      // Script files changed — recreate the validator to drop its stale
      // pifSolutionSummary (which accumulates diagnostics across runs), then
      // clear the service-level cache so the next validate() does a full pass.
      this.validator = new AgentCompiler(this.options);
      this.lastValidatedNodeMap.clear();
      this.lastDiagnostics = [];
    }
  }

  /**
   * Drop everything that carries over between passes: the compiler's own
   * accumulated `pifSolutionSummary`, the node map the incremental diff is
   * computed against, and the cached diagnostics `validate()` re-serves when a
   * diff comes out empty.
   *
   * Two kinds of caller, both legitimate:
   * - the solution context changed (a different bot/version), which
   *   `validate()` below detects and handles itself;
   * - a pass ended with the compiler compromised (PSTUDIO-4120), so whatever it
   *   accumulated is suspect and must not be served to the next pass.
   *
   * Blanking `lastValidatedSolutionVersionId` is NOT redundant bookkeeping, and
   * an earlier revision of this ticket removed it as though it were. It is the
   * only thing that heals a reset racing a pass already in flight, and
   * `validateAndSync` is not serialised, so those overlap:
   *
   *   1. pass A is inside `validateJson`, awaiting;
   *   2. pass B (or a catch, or an empty-table exit) calls `reset()` — a fresh
   *      compiler, an empty node map;
   *   3. A finishes and unconditionally writes `lastValidatedNodeMap` back,
   *      restoring the map the reset had just dropped;
   *   4. the compiler is now EMPTY while the map claims every node is already
   *      validated, so the next pass takes the incremental branch and asks only
   *      about the one node that changed. Every other finding in the solution
   *      silently disappears and the badge goes clean — this ticket's fail-open,
   *      arriving from the side.
   *
   * With the id blanked, step 4 instead sees `'' !== solutionVersionId`, resets
   * again and runs a FULL pass, so the desync cannot survive one round. The
   * second reset it costs is the price of that healing, and it is not a real
   * cost: constructing an `AgentCompiler` is one field assignment (measured at
   * ~0.0001 ms; Langium services are built inside `validateJson`, not here).
   *
   * A generation counter would be the precise fix — stamp the generation before
   * the first `await` and refuse to publish a stale pass's cache — and it is
   * worth doing when this service next changes. This is the cheap version that
   * cannot be forgotten.
   */
  reset(): void {
    this.validator = new AgentCompiler(this.options);
    this.lastValidatedNodeMap.clear();
    this.lastValidatedSolutionVersionId = '';
    this.lastDiagnostics = [];
  }

  /**
   * Validate rows using partial compilation when possible.
   * First call: full document. Subsequent calls: only changed/added nodes, removeNodes for deleted.
   */
  async validate(
    rows: TableNode[],
    solutionVersionId: string,
    prepareRowsDataForPifCompiler: PrepareRowsFn
  ): Promise<Diagnostic[]> {
    // Reset when solution changes
    if (solutionVersionId !== this.lastValidatedSolutionVersionId) {
      this.reset();
      this.lastValidatedSolutionVersionId = solutionVersionId;
    }

    if (rows.length === 0) {
      // A FULL reset, not just a map clear (PSTUDIO-4120). An empty row set
      // means nothing the compiler accumulated is still true, and clearing the
      // map alone left `pifSolutionSummary` holding every node that had just
      // gone away: `removeNodes` is derived from the map, so those nodes could
      // never be retracted, and the next pass — seeing an empty map — ran as a
      // "first" full pass against a compiler still carrying them. Measured on
      // agent-compiler 1.11.0: a solution whose one remaining node points at a
      // deleted one came back CLEAN, while the same input on a fresh compiler
      // reported `Refers to node #2, which does not exist`.
      //
      // Reachable since the caller began sending the payload MINUS rows a
      // delete has claimed: mark every row for deletion (Delete All Rows, or a
      // group delete) and this set is empty while the table is not.
      this.reset();
      return [];
    }

    const dataForCompiling = prepareRowsDataForPifCompiler(rows);
    let currentNodes: Array<Record<string, unknown> & { id: string }>;

    try {
      currentNodes = JSON.parse(dataForCompiling) as Array<
        Record<string, unknown> & { id: string }
      >;
    } catch {
      // If parsing fails, fall back to full validation
      const diagnostics = (await this.validator.validateJson(
        dataForCompiling
      )) as Diagnostic[];
      this.lastValidatedNodeMap.clear();
      this.lastDiagnostics = diagnostics;
      return diagnostics;
    }

    // Compute diff
    const currentIdSet = new Set(currentNodes.map((n) => n.id));
    const removedIds: string[] = [];
    for (const id of this.lastValidatedNodeMap.keys()) {
      if (!currentIdSet.has(id)) {
        removedIds.push(id);
      }
    }

    const changedOrAddedIds = new Set<string>();
    for (const node of currentNodes) {
      const { id } = node;
      const lastNode = this.lastValidatedNodeMap.get(id);
      if (!lastNode || !isEqual(lastNode, node)) {
        changedOrAddedIds.add(id);
      }
    }

    let diagnostics: Diagnostic[] = this.lastDiagnostics;

    if (this.lastValidatedNodeMap.size === 0) {
      // First run — full validation
      diagnostics = (await this.validator.validateJson(
        dataForCompiling
      )) as Diagnostic[];
    } else if (removedIds.length === 0 && changedOrAddedIds.size === 0) {
      // No changes — return cached diagnostics
      return this.lastDiagnostics;
    } else {
      // Partial validation
      if (removedIds.length > 0) {
        diagnostics = (await this.validator.removeNodes(
          removedIds
        )) as Diagnostic[];
      }
      if (changedOrAddedIds.size > 0) {
        const changedOrAddedRows: TableNode[] = rows.filter((r) =>
          changedOrAddedIds.has(r.id as string)
        );
        const changedData = prepareRowsDataForPifCompiler(changedOrAddedRows);
        diagnostics = (await this.validator.validateJson(
          changedData
        )) as Diagnostic[];
      } else if (removedIds.length === 0) {
        diagnostics = this.lastDiagnostics;
      }
    }

    this.lastValidatedNodeMap = new Map(
      currentNodes.map((n) => [n.id, n as Record<string, unknown>])
    );
    this.lastDiagnostics = diagnostics;
    return diagnostics;
  }
}
